import {
  actionCount,
  canRun,
  PLANS,
  formatSaved,
  savingsClaim,
  savedSeconds,
  estimatedMinutes,
  isAction,
  stepTitle,
  type MeResponse,
  type RemoteGuide,
  type Step,
  type Locale,
  type SuggestResponse,
} from '@nolens/domain';
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  ImageEditorDialog,
  Menu,
  MenuItem,
  Select,
  pixelateImage,
  Shot,
  SiteChips,
  Switch,
  useLocale,
  useToast,
  type ImageEdit,
} from '@nolens/ui';
import { LOCALE_NAMES, LOCALES } from '@nolens/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { CommentThread } from '../components/Comments';
import { StatsStrip } from '../components/Stats';
import { api, apiUpload } from '../lib/api';
import { brandStyle, useMemberBrandAssets } from '../lib/brand';
import {
  embedCode,
  exportHtml,
  exportMarkdown,
  exportPptx,
  MissingImages,
  printPdf,
  type ExportOptions,
} from '../lib/exports';
import { useBlocker, useNavigate, useParams } from 'react-router';
import { GuideSteps, ZoomAll } from '../components/GuideSteps';
import { QueryView } from '../components/Query';
import { ApiError } from '../lib/api';
import { guideImageBlob, prepareReplacement, useGuideImage } from '../lib/images';
import { reportError } from '../lib/errors';
import { useGuideDraft, type Draft } from '../lib/draft';
import { detectExtension, playOnExtension } from '../lib/extension';
import { macLinked, openMac } from '../lib/mac';
import {
  useBrand,
  useComments,
  useCopyGuide,
  useDeleteGuide,
  useGuide,
  usePatchGuide,
  useStats,
  useSubscription,
} from '../lib/queries';
import { spaceCrumbs, WithMe } from './workspace';
import { guideUiText } from '../i18n/guide';
import { publicPaths } from '../i18n/landing';
import { spaceName } from '../lib/locale';
import { ProTag } from '../components/PlanLimit';

export function GuidePage() {
  const { guideId } = useParams();
  const guide = useGuide(guideId);
  const locale = useLocale();
  const untitled = guideUiText(locale).untitled;
  return (
    <QueryView q={guide}>
      {(g) => (
        <WithMe
          current={g.workspaceId}
          crumbs={(me) => spaceCrumbs(me, g.workspaceId, locale, { label: g.title || untitled })}
        >
          {(me) => <GuideEditor key={g.id} guide={g} me={me} />}
        </WithMe>
      )}
    </QueryView>
  );
}

function PrivateShot({ guideId, step, zoom }: { guideId: string; step: Step; zoom: boolean }) {
  const src = useGuideImage(guideId, step.imageId);
  return <Shot step={step} src={src} zoom={zoom} />;
}

/**
 * Editor da imagem na web: destaques, setas, recorte e ponto vão para o rascunho; o desfoque
 * é feito aqui no browser e a captura nova é enviada logo (a anterior é apagada da conta).
 */
function WebImageEditor({
  guide,
  baseVersion,
  step,
  onClose,
  onSaved,
}: {
  guide: RemoteGuide;
  /** A versão em que o rascunho se baseia (num conflito, a captura não muda). */
  baseVersion: number;
  step: Step;
  onClose: () => void;
  /** `image`: a imagem já mudou na conta (desfocada ou substituída), na versão `g`. */
  onSaved: (patch: Partial<Step>, image: 'blurred' | 'replaced' | null, g?: RemoteGuide) => void;
}) {
  const src = useGuideImage(guide.id, step.imageId);
  const [saving, setSaving] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const t = guideUiText(useLocale());

  /** Envia a imagem nova do passo e devolve o guia na versão nova (null: não ficou). */
  async function upload(blob: Blob, failed: string): Promise<RemoteGuide | null> {
    setSaving(true);
    try {
      return await apiUpload<RemoteGuide>(
        `/v1/guides/${guide.id}/steps/${encodeURIComponent(step.id)}/image?baseVersion=${baseVersion}`,
        blob,
      );
    } catch (e) {
      if (e instanceof ApiError && e.code === 'VERSION_CONFLICT')
        toast(t.changedElsewhere, 'critical');
      else toast(failed, 'critical', { detail: e instanceof ApiError ? e.message : undefined });
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function save({ crop, point, annotations, blurs }: ImageEdit) {
    const patch: Partial<Step> = { crop, point, annotations };
    if (!blurs.length || !step.imageId) return onSaved(patch, null);
    let blob: Blob;
    try {
      blob = await pixelateImage(await guideImageBlob(guide.id, step.imageId), blurs);
    } catch (e) {
      reportError(e);
      toast(t.couldNotBlur, 'critical');
      return;
    }
    const g = await upload(blob, t.couldNotBlur);
    const imageId = g?.steps.find((s) => s.id === step.id)?.imageId;
    if (g) onSaved({ ...patch, imageId }, 'blurred', g);
  }

  async function replace(file: File) {
    let img: Awaited<ReturnType<typeof prepareReplacement>>;
    try {
      img = await prepareReplacement(file);
    } catch {
      toast(t.notAnImage, 'critical');
      return;
    }
    const g = await upload(img.blob, t.couldNotReplace);
    if (!g) return;
    const imageId = g.steps.find((s) => s.id === step.id)?.imageId;
    // Outra imagem: o recorte, o ponto do clique e as anotações da antiga já não servem
    onSaved(
      {
        imageId,
        viewport: { w: img.width, h: img.height },
        crop: undefined,
        point: undefined,
        box: undefined,
        annotations: undefined,
      },
      'replaced',
      g,
    );
  }

  return (
    <ImageEditorDialog
      step={step}
      src={src}
      onClose={onClose}
      onSave={(e) => void save(e)}
      saving={saving}
      blurHint={t.blurHint}
      extraTools={
        <>
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            data-testid="replace-image-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void replace(f);
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={saving}
            title={t.replaceImageHint}
            onClick={() => fileInput.current?.click()}
          >
            {t.replaceImage}
          </Button>
        </>
      }
    />
  );
}

function GuideEditor({ guide, me }: { guide: RemoteGuide; me: MeResponse }) {
  const { draft, setDraft, base, dirty, stale, restored, rebase, forget } = useGuideDraft(guide);
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const patch = usePatchGuide(guide.id);
  const copy = useCopyGuide(guide.id);
  const del = useDeleteGuide();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const locale = useLocale();
  const t = guideUiText(locale);
  const pricingPath = publicPaths(locale).pricing;
  const ws = me.workspaces.find((w) => w.id === guide.workspaceId);
  // Funcionalidades do plano do espaço (a API aplica-as; aqui só se mostram)
  const features = PLANS[ws?.plan ?? 'free'];
  // Alterado noutro sítio: visto ao recarregar o guia da conta, ou recusado ao guardar
  const conflict =
    stale || (patch.error instanceof ApiError && patch.error.code === 'VERSION_CONFLICT');

  // Sair com alterações por guardar: fechar ou recarregar a página (o Chrome pergunta) e
  // ligações da app (o diálogo abaixo). Apagar o guia ou abrir a cópia não pergunta.
  const leaving = useRef(false);
  useEffect(() => {
    if (!dirty) return;
    const ask = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [dirty]);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && !leaving.current && currentLocation.pathname !== nextLocation.pathname,
  );
  const leaveTo = (path: string) => {
    leaving.current = true;
    forget();
    navigate(path);
  };

  /** Num conflito: descarta o rascunho e mostra a versão da conta. */
  const discardMine = async () => {
    const latest = await api<RemoteGuide>(`/v1/guides/${guide.id}`);
    qc.setQueryData(['guide', guide.id], latest);
    rebase(latest);
    patch.reset();
  };

  /** Num conflito: o rascunho vai para um guia novo; o original fica como está. */
  const saveAsCopy = () =>
    copy.mutate(
      { ...draft, title: t.copyTitle(draft.title) },
      {
        onSuccess: (g) => {
          toast(t.savedAsCopy, 'positive');
          leaveTo(`/g/${g.id}`);
        },
        onError: (e) =>
          toast(t.couldNotCopy, 'critical', {
            detail: e instanceof ApiError ? e.message : undefined,
            action: { label: t.retry, onClick: saveAsCopy },
          }),
      },
    );
  const brand = useBrand(guide.workspaceId).data ?? null;
  const assets = useMemberBrandAssets(guide.workspaceId, brand);
  const comments = useComments(guide.id).data ?? [];
  const stats = useStats(guide.id);
  const follow = useSubscription(guide.id);
  const [exporting, setExporting] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // Exportação parada porque faltam capturas: pergunta se tenta outra vez ou exporta sem elas
  const [missing, setMissing] = useState<{
    kind: string;
    fn: (o: ExportOptions) => Promise<void>;
    missing: number;
    total: number;
  } | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [playing, setPlaying] = useState<'guide' | 'run' | null>(null);
  const [editingImage, setEditingImage] = useState<string | null>(null);
  const editingStep = draft.steps.find((s) => s.id === editingImage);
  // Gravado no Mac (ADR-G25): guia-se e executa-se na app de Mac, não na extensão
  const onMac = guide.source === 'mac';
  const canPlay = onMac
    ? draft.steps.some(isAction)
    : draft.steps.some((s) => s.kind !== 'text' && /^https?:/.test(s.url));

  // Guiar ou executar na página: é a extensão que abre a aplicação e faz/realça cada passo.
  // Guia o que está guardado na conta: com alterações por guardar, pede para guardar e não
  // começa (seria um guia diferente do que se vê aqui).
  const play = async (auto: boolean) => {
    if (dirty) {
      toast(t.saveFirst);
      return;
    }
    if (onMac) {
      if (!macLinked(me)) {
        toast(t.installMacApp);
        navigate('/mac');
        return;
      }
      openMac(auto ? 'executar' : 'guiar', { guia: guide.id });
      return;
    }
    setPlaying(auto ? 'run' : 'guide');
    try {
      const ext = await detectExtension();
      if (!ext) {
        toast(t.installExtension);
        navigate('/extensao');
        return;
      }
      if (!ext.linkedEmail) {
        navigate('/extensao');
        return;
      }
      const r = await playOnExtension(guide.id, auto);
      if (!r.ok)
        toast(t.couldNotStart, 'critical', {
          detail: r.error,
          action: { label: t.retry, onClick: () => void play(auto) },
        });
    } finally {
      setPlaying(null);
    }
  };

  // Uma leitura por sessão do browser e por guia.
  useEffect(() => {
    const key = 'viewed:' + guide.id;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      // armazenamento indisponível: conta na mesma
    }
    void api(`/v1/guides/${guide.id}/view`, { method: 'POST' }).catch(() => undefined);
  }, [guide.id]);

  const source = {
    id: guide.id,
    workspaceId: guide.workspaceId,
    title: draft.title,
    description: draft.description,
    language: draft.language,
    steps: draft.steps,
  };
  const runExport = async (
    kind: string,
    fn: (o: ExportOptions) => Promise<void>,
    opts: ExportOptions = {},
  ) => {
    setMissing(null);
    setExporting(kind);
    setProgress(null);
    try {
      await fn({ ...opts, onProgress: (done, total) => setProgress({ done, total }) });
    } catch (e) {
      if (e instanceof MissingImages) setMissing({ kind, fn, missing: e.missing, total: e.total });
      else
        toast(t.couldNotExport, 'critical', {
          detail: e instanceof ApiError ? e.message : undefined,
          action: { label: t.retry, onClick: () => void runExport(kind, fn, opts) },
        });
    } finally {
      setExporting(null);
    }
  };

  const save = () =>
    patch.mutate(
      {
        baseVersion: base.version,
        title: draft.title,
        description: draft.description,
        language: draft.language,
        steps: draft.steps,
      },
      {
        onSuccess: (g) => {
          rebase(g);
          toast(t.changesSaved, 'positive');
        },
      },
    );

  return (
    <article className="mx-auto max-w-3xl" style={brandStyle(brand, assets.fontReady)}>
      {assets.logo && (
        <img src={assets.logo} alt="" className="mb-4 h-8 w-auto max-w-[200px]" data-brand-logo />
      )}
      {guide.canEdit ? (
        <>
          <input
            aria-label={t.guideTitle}
            className="block w-full bg-transparent text-[28px] font-semibold tracking-tight outline-none"
            value={draft.title}
            placeholder={t.untitled}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
          <textarea
            aria-label={t.guideDescription}
            rows={Math.max(1, draft.description.split('\n').length)}
            className="mt-2 block w-full resize-none bg-transparent text-[16px] leading-relaxed text-text-2 outline-none placeholder:text-text-3"
            value={draft.description}
            placeholder={t.descriptionPlaceholder}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </>
      ) : (
        <>
          <h1 className="text-[28px] font-semibold tracking-tight">{guide.title || t.untitled}</h1>
          {guide.description && (
            <p className="mt-2 whitespace-pre-wrap text-[16px] leading-relaxed text-text-2">
              {guide.description}
            </p>
          )}
        </>
      )}

      <p className="mt-3 text-[13.5px] text-text-3">
        {t.meta(actionCount(draft), estimatedMinutes(draft))}
        {actionCount(draft) > 0 && (
          <span title={savingsClaim(locale)} data-saved>
            {t.saved(formatSaved(savedSeconds(actionCount(draft)), locale))}
          </span>
        )}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <SiteChips guide={draft} />
        {guide.canEdit ? (
          <label className="no-print inline-flex items-center gap-2 text-[13px] text-text-2">
            {t.guideLanguage}
            <select
              aria-label={t.guideLanguage}
              className="h-8 rounded-[8px] bg-surface-1 px-2 text-[13px] text-text-1 shadow-[inset_0_0_0_1px_var(--border-strong)]"
              value={draft.language}
              onChange={(e) => setDraft({ ...draft, language: e.target.value as Locale })}
              data-guide-language
            >
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {LOCALE_NAMES[l]}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {stats.data && (
        <div className="no-print mt-4">
          <StatsStrip stats={stats.data} />
        </div>
      )}

      <div className="no-print mt-5 flex flex-wrap gap-2">
        {canPlay && (
          <Button
            size="sm"
            loading={playing === 'guide'}
            onClick={() => void play(false)}
            title={onMac ? t.guideOnMacTip : t.guideTitleTip}
            data-play={onMac ? 'mac' : 'extension'}
          >
            {onMac ? t.guideOnMac : t.guide}
          </Button>
        )}
        {canPlay && canRun(guide) && (
          <Button
            size="sm"
            variant="secondary"
            loading={playing === 'run'}
            onClick={() => (features.run ? void play(true) : navigate(pricingPath))}
            title={onMac ? t.runOnMacTip : t.runTip}
          >
            {onMac ? t.runOnMac : t.run}
            {!features.run && <ProTag />}
          </Button>
        )}
        {guide.canEdit && (
          <Button size="sm" variant="secondary" onClick={() => setShareOpen(true)}>
            {t.share}
          </Button>
        )}
        <Menu
          id="export-menu"
          label={t.export}
          trigger={
            // O Menu já é um botão: o gatilho é só o aspeto.
            <span
              className="inline-flex h-9 items-center rounded-full px-4 text-sm font-medium shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-surface-1"
              aria-busy={!!exporting}
            >
              {!exporting
                ? t.export
                : progress?.total
                  ? t.exportingProgress(progress.done, progress.total)
                  : t.exporting}
            </span>
          }
        >
          <MenuItem
            popoverId="export-menu"
            onClick={() =>
              void runExport('pdf', (o) =>
                printPdf(source, brand, ws ? spaceName(ws, locale) : '', o),
              )
            }
          >
            {t.pdf}
          </MenuItem>
          <MenuItem
            popoverId="export-menu"
            onClick={() =>
              features.editorialExports
                ? void runExport('html', (o) =>
                    exportHtml(source, brand, ws ? spaceName(ws, locale) : '', o),
                  )
                : navigate(pricingPath)
            }
          >
            HTML
            {!features.editorialExports && <ProTag />}
          </MenuItem>
          <MenuItem
            popoverId="export-menu"
            onClick={() =>
              features.editorialExports
                ? void runExport('md', (o) => exportMarkdown(source, brand, o))
                : navigate(pricingPath)
            }
          >
            Markdown (.zip)
            {!features.editorialExports && <ProTag />}
          </MenuItem>
          <MenuItem
            popoverId="export-menu"
            onClick={() =>
              features.editorialExports
                ? void runExport('pptx', (o) =>
                    exportPptx(source, brand, ws ? spaceName(ws, locale) : '', o),
                  )
                : navigate(pricingPath)
            }
          >
            PowerPoint
            {!features.editorialExports && <ProTag />}
          </MenuItem>
        </Menu>
        {guide.canEdit && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => (features.ai ? setSuggestOpen(true) : navigate(pricingPath))}
          >
            {t.suggest}
            {!features.ai && <ProTag />}
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          loading={follow.isPending}
          onClick={() => follow.mutate(!guide.subscribed)}
          aria-pressed={guide.subscribed}
          title={t.followTip}
        >
          {guide.subscribed ? t.following : t.follow}
        </Button>
        {guide.canEdit && (
          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
            {t.delete}
          </Button>
        )}
      </div>

      {restored && !conflict && (
        <div className="mt-5" data-draft-restored>
          <Banner tone="info">{t.restored}</Banner>
        </div>
      )}
      {conflict && (
        <div className="mt-5" data-conflict>
          <Banner tone="attention">
            <p className="font-medium">{t.conflict}</p>
            <p className="mt-1">{t.conflictHint}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" loading={copy.isPending} onClick={saveAsCopy}>
                {t.saveAsCopy}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => void discardMine()}>
                {t.discardMine}
              </Button>
            </div>
          </Banner>
        </div>
      )}

      <div className="mt-6">
        {guide.canEdit && (
          <ZoomAll steps={draft.steps} onChange={(steps) => setDraft({ ...draft, steps })} />
        )}
        {draft.steps.length ? (
          <GuideSteps
            steps={draft.steps}
            locale={draft.language}
            shot={(s, zoom) => <PrivateShot guideId={guide.id} step={s} zoom={zoom} />}
            onChange={guide.canEdit ? (steps) => setDraft({ ...draft, steps }) : undefined}
            onEditImage={guide.canEdit ? (st) => setEditingImage(st.id) : undefined}
            below={(st) => (
              <CommentThread
                guideId={guide.id}
                stepId={st.id}
                comments={comments}
                canModerate={guide.canEdit}
                compact
              />
            )}
          />
        ) : (
          <p className="text-text-2">{t.noSteps}</p>
        )}
      </div>

      <section className="no-print mt-8">
        <h2 className="text-lg font-semibold">{t.comments}</h2>
        <CommentThread
          guideId={guide.id}
          stepId={null}
          comments={comments}
          canModerate={guide.canEdit}
        />
      </section>

      {guide.canEdit && dirty && (
        <div className="no-print sticky bottom-4 mt-6 flex items-center justify-end gap-2 rounded-[14px] bg-surface-1 p-3 shadow-[0_8px_24px_rgba(0,0,0,.12),inset_0_0_0_1px_var(--border)]">
          <span className="mr-auto text-sm text-text-2">{t.unsaved}</span>
          <Button size="sm" variant="ghost" onClick={() => setDraft(base.content)}>
            {t.discard}
          </Button>
          <Button size="sm" loading={patch.isPending} disabled={conflict} onClick={save}>
            {t.save}
          </Button>
        </div>
      )}

      {editingStep && (
        <WebImageEditor
          guide={guide}
          baseVersion={base.version}
          step={editingStep}
          onClose={() => setEditingImage(null)}
          onSaved={(p, image, g) => {
            // A captura nova já ficou na conta (versão nova, que passa a ser a base); o resto
            // vai no rascunho.
            const next = {
              ...draft,
              steps: draft.steps.map((s) => (s.id === editingStep.id ? { ...s, ...p } : s)),
            };
            if (g) {
              qc.setQueryData(['guide', guide.id], g);
              rebase(g, next);
            } else setDraft(next);
            setEditingImage(null);
            if (image === 'blurred') toast(t.blurApplied, 'positive');
            if (image === 'replaced') toast(t.imageReplaced, 'positive');
          }}
        />
      )}
      {shareOpen && <ShareDialog guide={guide} me={me} onClose={() => setShareOpen(false)} />}
      {suggestOpen && (
        <SuggestDialog
          guideId={guide.id}
          draft={draft}
          onClose={() => setSuggestOpen(false)}
          onApply={(next) => {
            setDraft(next);
            setSuggestOpen(false);
            toast(t.suggestionsApplied, 'positive');
          }}
        />
      )}
      <Dialog
        open={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        title={t.leaveTitle}
        description={t.leaveHint}
        footer={
          <>
            <Button variant="ghost" onClick={() => blocker.reset?.()}>
              {t.stay}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                forget();
                blocker.proceed?.();
              }}
            >
              {t.leaveWithoutSaving}
            </Button>
          </>
        }
      >
        <span />
      </Dialog>
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t.deleteTitle}
        description={t.deleteHint}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t.cancel}
            </Button>
            <Button
              variant="danger"
              loading={del.isPending}
              onClick={() =>
                del.mutate(guide.id, { onSuccess: () => leaveTo(`/w/${guide.workspaceId}`) })
              }
            >
              {t.delete}
            </Button>
          </>
        }
      >
        <span />
      </Dialog>
      <Dialog
        open={!!missing}
        onClose={() => setMissing(null)}
        title={missing ? t.missingImagesTitle(missing.missing) : ''}
        description={missing ? t.missingImagesHint(missing.missing, missing.total) : undefined}
        footer={
          missing && (
            <>
              <Button
                variant="ghost"
                onClick={() => void runExport(missing.kind, missing.fn, { skipMissing: true })}
              >
                {t.exportWithoutImages}
              </Button>
              <Button onClick={() => void runExport(missing.kind, missing.fn)}>
                {t.retryExport}
              </Button>
            </>
          )
        }
      >
        <span />
      </Dialog>
    </article>
  );
}

function ShareDialog({
  guide,
  me,
  onClose,
}: {
  guide: RemoteGuide;
  me: MeResponse;
  onClose: () => void;
}) {
  const patch = usePatchGuide(guide.id);
  const toast = useToast();
  const navigate = useNavigate();
  const locale = useLocale();
  const t = guideUiText(locale);
  const link = guide.shareToken ? `${window.location.origin}/s/${guide.shareToken}` : null;
  const targets = me.workspaces.filter((w) => w.role !== 'viewer');
  const copy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    toast(t.linkCopied, 'positive');
  };
  return (
    <Dialog open onClose={onClose} title={t.share} description={t.shareHint}>
      <div className="flex flex-col gap-5">
        <Switch
          checked={guide.visibility === 'link'}
          onChange={(on) =>
            patch.mutate({ baseVersion: guide.version, visibility: on ? 'link' : 'workspace' })
          }
          label={t.anyoneWithLink}
          description={t.anyoneWithLinkHint}
        />
        {link && (
          <div className="flex gap-2">
            <input
              readOnly
              aria-label={t.publicLink}
              value={link}
              className="h-10 min-w-0 flex-1 rounded-[10px] bg-surface-2 px-3 text-[14px]"
              onFocus={(e) => e.target.select()}
            />
            <Button size="sm" onClick={() => void copy()}>
              {t.copy}
            </Button>
          </div>
        )}
        {guide.shareToken && (
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {t.embed}
            <textarea
              readOnly
              rows={3}
              className="rounded-[10px] bg-surface-2 p-3 font-mono text-[12.5px]"
              value={embedCode(window.location.origin, guide.shareToken, guide.title)}
              onFocus={(e) => e.target.select()}
            />
          </label>
        )}
        {targets.length > 1 && (
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {t.space}
            <Select
              value={guide.workspaceId}
              onChange={(e) =>
                patch.mutate(
                  { baseVersion: guide.version, workspaceId: e.target.value },
                  { onSuccess: (g) => navigate(`/g/${g.id}`, { replace: true }) },
                )
              }
            >
              {targets.map((w) => (
                <option key={w.id} value={w.id}>
                  {spaceName(w, locale)}
                </option>
              ))}
            </Select>
            <span className="font-normal text-text-2">{t.teamSpaceHint}</span>
          </label>
        )}
        {patch.error instanceof ApiError && <Banner tone="critical">{patch.error.message}</Banner>}
      </div>
    </Dialog>
  );
}

/** Propostas de texto por IA: o autor escolhe o que aplica (fica por guardar). */
function SuggestDialog({
  guideId,
  draft,
  onClose,
  onApply,
}: {
  guideId: string;
  draft: Draft;
  onClose: () => void;
  onApply: (d: Draft) => void;
}) {
  const t = guideUiText(useLocale());
  const run = useMutation({
    mutationFn: () => api<SuggestResponse>(`/v1/guides/${guideId}/suggest`, { method: 'POST' }),
  });
  const [pick, setPick] = useState({
    title: true,
    description: true,
    steps: true,
    stepDescriptions: true,
  });
  // Um pedido ao abrir (a ref evita o segundo do StrictMode em desenvolvimento).
  const started = useRef(false);
  const { mutate } = run;
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    mutate();
  }, [mutate]);
  const r = run.data;
  const changed = r
    ? r.steps.filter((x) => {
        const s = draft.steps.find((y) => y.id === x.id);
        return s && x.title.trim() && x.title !== stepTitle(s, draft.language);
      })
    : [];
  // Descrições só para passos que continuam sem descrição (a da pessoa nunca é substituída).
  const described = r
    ? r.steps.filter((x) => {
        const s = draft.steps.find((y) => y.id === x.id);
        return s && x.description?.trim() && !s.description.trim();
      })
    : [];
  const stepName = (id: string) => {
    const x = r?.steps.find((y) => y.id === id);
    const s = draft.steps.find((y) => y.id === id);
    return pick.steps && x?.title.trim() ? x.title.trim() : s ? stepTitle(s, draft.language) : '';
  };
  const apply = () => {
    if (!r) return;
    const titles = new Map(pick.steps ? changed.map((x) => [x.id, x.title.trim()]) : []);
    const descs = new Map(
      pick.stepDescriptions ? described.map((x) => [x.id, x.description!.trim()]) : [],
    );
    onApply({
      title: pick.title && r.title.trim() ? r.title.trim() : draft.title,
      description:
        pick.description && r.description.trim() ? r.description.trim() : draft.description,
      language: draft.language,
      steps: draft.steps.map((s) =>
        titles.has(s.id) || descs.has(s.id)
          ? {
              ...s,
              ...(titles.has(s.id) ? { title: titles.get(s.id)! } : {}),
              ...(descs.has(s.id) ? { description: descs.get(s.id)! } : {}),
            }
          : s,
      ),
    });
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={t.suggestTitle}
      description={t.suggestHint}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t.cancel}
          </Button>
          <Button disabled={!r} onClick={apply}>
            {t.apply}
          </Button>
        </>
      }
    >
      {run.isPending && <p className="text-text-2">{t.thinking}</p>}
      {run.error instanceof ApiError && (
        <Banner tone={run.error.code === 'NOT_CONFIGURED' ? 'info' : 'critical'}>
          {run.error.code === 'NOT_CONFIGURED' ? t.aiNotConfigured : run.error.message}
        </Banner>
      )}
      {r && (
        <div className="flex flex-col gap-4 text-[14.5px]">
          <Checkbox
            checked={pick.title}
            onChange={(e) => setPick({ ...pick, title: e.target.checked })}
            label={
              <span>
                {t.titleLabel} <strong>{r.title}</strong>
              </span>
            }
          />
          <Checkbox
            checked={pick.description}
            onChange={(e) => setPick({ ...pick, description: e.target.checked })}
            label={
              <span>
                {t.descriptionLabel} {r.description}
              </span>
            }
          />
          <Checkbox
            checked={pick.steps}
            onChange={(e) => setPick({ ...pick, steps: e.target.checked })}
            label={t.stepTitles(changed.length)}
          />
          {changed.length > 0 && (
            <ul className="ml-7 flex list-disc flex-col gap-1 pl-4 text-text-2">
              {changed.map((x) => (
                <li key={x.id}>{x.title}</li>
              ))}
            </ul>
          )}
          <Checkbox
            checked={pick.stepDescriptions}
            onChange={(e) => setPick({ ...pick, stepDescriptions: e.target.checked })}
            label={t.stepDescriptions(described.length)}
          />
          {described.length > 0 && (
            <ul className="ml-7 flex flex-col gap-2 pl-4 text-text-2" data-suggested-descriptions>
              {described.map((x) => (
                <li key={x.id}>
                  <span className="font-medium text-text-1">{stepName(x.id)}</span>
                  <br />
                  {x.description}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Dialog>
  );
}
